import { useCallback, useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { Button } from '../../../../shared/components/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

/**
 * M05 L5.04 — Holiday + business calendar.
 *
 * Holidays are reference data in the as-built M05 model, so this screen
 * manages a `HOLIDAY` lookup category: each value's code is the holiday date
 * (YYYY-MM-DD) and its description is the holiday name. Ensures the category
 * exists (creating it lazily) and lets the admin add / view holidays.
 * No schema change — reuses the generic lookup backend.
 */
interface LookupCategory {
  readonly lookupCategoryId: string;
  readonly lookupCategoryCode: string;
}

interface LookupValue {
  readonly lookupValueId: string;
  readonly lookupValueCode: string;
  readonly description: string;
  readonly recordStatusCode: string;
}

const CATEGORY_CODE = 'HOLIDAY';

export function HolidayCalendarPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [rows, setRows] = useState<LookupValue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    setCategoryId(null);
    try {
      const cats = await apiRequest<LookupCategory[]>('/master/lookup-categories', { token });
      let cat = (Array.isArray(cats) ? cats : []).find((c) => c.lookupCategoryCode === CATEGORY_CODE);
      if (!cat) {
        cat = await apiRequest<LookupCategory>('/master/lookup-categories', {
          method: 'POST', token,
          body: { lookupCategoryCode: CATEGORY_CODE, categoryName: 'Holiday calendar' },
        });
      }
      setCategoryId(cat.lookupCategoryId);
      const vals = await apiRequest<LookupValue[]>(
        `/master/lookup-categories/${encodeURIComponent(cat.lookupCategoryId)}/values`, { token },
      );
      const sorted = (Array.isArray(vals) ? vals : [])
        .slice()
        .sort((a, b) => a.lookupValueCode.localeCompare(b.lookupValueCode));
      setRows(sorted);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const onAdd = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!categoryId) return;
    setSaving(true);
    setFormError(null);
    try {
      await apiRequest(`/master/lookup-categories/${encodeURIComponent(categoryId)}/values`, {
        method: 'POST', token,
        body: { lookupCategoryId: categoryId, lookupValueCode: holidayDate, description: holidayName.trim(), sortOrder: 0 },
      });
      setHolidayDate(''); setHolidayName('');
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [categoryId, holidayDate, holidayName, token, load]);

  return (
    <main aria-label="holiday-calendar">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <Link to="/admin/mdm/lookups" data-testid="back-catalogue">← {t('mdm.kit.masterData')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('mdm.holiday.title')}</h1>
      </div>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('mdm.holiday.intro')}
      </p>

      <form onSubmit={onAdd} aria-label="add-holiday-form"
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="holiday-date" style={{ fontSize: 12 }}>{t('mdm.col.date')}</label>
          <DateInput id="holiday-date" type="date" value={holidayDate} onValueChange={(nextValue) => setHolidayDate(nextValue)} required
                 style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="holiday-name" style={{ fontSize: 12 }}>{t('mdm.holiday.name')}</label>
          <input id="holiday-name" value={holidayName} onChange={(e) => setHolidayName(e.target.value)} required
                 style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </div>
        <Button type="submit" variant="primary" data-testid="add-holiday" disabled={saving || !categoryId}
                style={{ padding: '6px 14px', borderRadius: 4 }}>
          {saving ? t('mdm.common.adding') : t('mdm.holiday.addHoliday')}
        </Button>
      </form>
      {formError && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{formError}</p>}

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="holiday-empty" style={{ color: 'var(--color-text-muted)' }}>{t('mdm.holiday.empty')}</p>
      ) : (
        <DataGrid<any>
          caption="holiday-table"
          rows={rows}
          getRowId={(v) => v.lookupValueId}
          rowTestId={(v) => `holiday-row-${v.lookupValueId}`}
          columns={[
            { key: 'date', header: t('mdm.col.date'), value: (v) => v.lookupValueCode },
            { key: 'holiday', header: t('mdm.holiday.colHoliday'), value: (v) => v.description },
            { key: 'status', header: t('mdm.col.status'), value: (v) => v.recordStatusCode },
          ]}
        />
      )}
    </main>
  );
}
